/**
 * Forgot Password — navy+gold AI Search Portal landing.
 *
 * KOPS-511: the legacy CardForgotPassword page used the old cyan
 * "KairosIQ AI Neural Network" branding which doesn't match the rest
 * of the AISearchPortal domain. This component is a self-contained
 * navy+gold rewrite that mirrors the AcceptInvitationAIForward layout
 * so the reset flow feels like one coherent product.
 *
 * Mounted at /agent-portal/forgot-password inside the AISearchPortal
 * domain branch of Routes.tsx. The legacy page is kept for the main
 * Kairos branch.
 *
 * Backend is unchanged — POST /api/auth/forget-password with { email }.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../helpers/api';

// Navy+gold palette — mirrors shared/colors.ts from the hub chrome.
const colors = {
  navy:  '#0F1E35',
  navy2: '#162844',
  navy3: '#1E3557',
  gold:  '#C9952A',
  gold2: '#E8B44A',
  gold3: '#F5D080',
  white: '#FFFFFF',
  off:   '#E8EEF6',
  muted: '#8FA3BF',
  green: '#27ae60',
  red:   '#e74c3c',
};

const fonts = {
  display: "'Bebas Neue', Impact, 'Arial Black', sans-serif",
  body:    "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
};

const FONT_LINK =
  'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';

const ForgotPasswordAIForward: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setResult({ kind: 'err', msg: 'Please enter the email you use for the portal.' });
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axiosInstance.post('/auth/forget-password', { email });
      setResult({
        kind: 'ok',
        msg:
          res?.data?.message ||
          'If that email is on file, a reset link is on its way. Check your inbox.',
      });
      setEmail('');
    } catch (err: any) {
      setResult({
        kind: 'err',
        msg:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'We couldn\'t send the reset link. Please try again in a moment.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => navigate('/agent-portal/login');

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.navy,
        fontFamily: fonts.body,
        color: colors.off,
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <link href={FONT_LINK} rel="stylesheet" />

      <div
        style={{
          width: '100%',
          maxWidth: '520px',
          background: colors.navy2,
          border: `1px solid ${colors.navy3}`,
          borderTop: `4px solid ${colors.gold}`,
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
        }}
      >
        {/* Brand header */}
        <div
          style={{
            textAlign: 'center',
            padding: '40px 32px 24px',
            borderBottom: `1px solid ${colors.navy3}`,
          }}
        >
          <div
            style={{
              fontFamily: fonts.display,
              fontSize: '32px',
              letterSpacing: '3px',
              color: colors.white,
              marginBottom: '6px',
            }}
          >
            AI<span style={{ color: colors.gold2 }}>Search</span>Portal
          </div>
          <div
            style={{
              color: colors.muted,
              fontSize: '11px',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              marginBottom: '28px',
            }}
          >
            AI-Powered Real Estate Compliance Platform
          </div>

          {/* Gold key/lock icon */}
          <div
            style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 50%, ${colors.gold3} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px auto',
              fontSize: '32px',
              color: colors.navy,
              fontWeight: 900,
              boxShadow: '0 8px 24px rgba(201, 149, 42, 0.35)',
            }}
          >
            🔑
          </div>

          <h1
            style={{
              fontFamily: fonts.display,
              fontSize: '32px',
              letterSpacing: '1.5px',
              color: colors.white,
              margin: '0 0 8px',
              lineHeight: 1.15,
            }}
          >
            Forgot Your Password?
          </h1>
          <p
            style={{
              fontSize: '13px',
              color: colors.muted,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            Enter the email address on your account and we'll send you a
            secure link to set a new password.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
          <label
            htmlFor="email"
            style={{
              display: 'block',
              fontSize: '10px',
              color: colors.muted,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 700,
              marginBottom: '8px',
            }}
          >
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@brokerage.com"
            disabled={submitting}
            style={{
              width: '100%',
              background: colors.navy3,
              color: colors.white,
              border: `1px solid ${colors.navy3}`,
              borderRadius: '8px',
              padding: '14px 16px',
              fontSize: '14px',
              fontFamily: fonts.body,
              outline: 'none',
              marginBottom: '18px',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = colors.gold)}
            onBlur={(e) => (e.currentTarget.style.borderColor = colors.navy3)}
          />

          {result && (
            <div
              style={{
                background:
                  result.kind === 'ok' ? `${colors.green}22` : `${colors.red}22`,
                border: `1px solid ${
                  result.kind === 'ok' ? colors.green : colors.red
                }`,
                borderLeft: `3px solid ${
                  result.kind === 'ok' ? colors.green : colors.red
                }`,
                color: result.kind === 'ok' ? colors.green : colors.red,
                padding: '12px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                lineHeight: 1.5,
                marginBottom: '18px',
              }}
            >
              {result.kind === 'ok' ? '✉️ ' : '⚠️ '}
              {result.msg}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              background: submitting
                ? colors.muted
                : `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 50%, ${colors.gold3} 100%)`,
              color: colors.navy,
              border: 'none',
              borderRadius: '8px',
              padding: '14px 20px',
              fontSize: '13px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: fonts.body,
              boxShadow: submitting
                ? 'none'
                : '0 8px 24px rgba(201, 149, 42, 0.35)',
              transition: 'transform 0.15s ease',
            }}
          >
            {submitting ? 'Sending reset link…' : 'Request Reset Link →'}
          </button>

          <button
            type="button"
            onClick={handleBackToLogin}
            disabled={submitting}
            style={{
              width: '100%',
              background: 'transparent',
              color: colors.gold2,
              border: 'none',
              padding: '14px 0 0 0',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: fonts.body,
            }}
          >
            ← Back to Sign In
          </button>
        </form>

        {/* Footer */}
        <div
          style={{
            background: colors.navy,
            padding: '20px 32px',
            textAlign: 'center',
            borderTop: `1px solid ${colors.navy3}`,
          }}
        >
          <div
            style={{
              fontSize: '11px',
              color: colors.muted,
              lineHeight: 1.6,
              marginBottom: '6px',
            }}
          >
            🔒 Secure, compliant, and built for real estate professionals
          </div>
          <div style={{ fontSize: '10px', color: colors.muted }}>
            © 2026 AI Search Portal. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordAIForward;
